import { Box, Chip, Link, styled } from "@mui/material";
import { Dayjs } from "dayjs";
import { AVAILABLE_BITRATES } from "./TriggerConstants";
import { CheckboxState } from "../context/useContentContext";
import {
  GraphDataApiResponse,
  JobDetails,
  PresetJobHeader,
} from "./typeConstants";
import theme from "../styles/theme";
import copyIcon from "../assets/copyIcon.png";
import {
  CONSTANTS,
  DAYS_SHORTHANDS,
  HOURLY_SHORTHANDS,
  IMAGE_BASE_URL,
} from "./constants";
import { CopyToClipboard } from "./getcopytoClipboard";

export const CustomChip = styled(Chip)(({ theme }) => ({
  position: "relative",
  borderRadius: "0 4px 4px 0",
  clipPath: "polygon(15% 0%, 100% 0%, 100% 100%, 15% 100%, 0% 50%)",
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(2),
  marginLeft: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&::before": {
    content: '""',
    position: "absolute",
    left: "15px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "8px",
    height: "8px",
    backgroundColor: "white",
    borderRadius: "50%",
  },
  backgroundColor: "#CCFAF7",
}));

export function formatDate(dateObj: Dayjs | null): string {
  if (!dateObj) {
    return "";
  }

  const day = dateObj.date();
  const month = dateObj.month() + 1;
  const year = dateObj.year();

  const paddedDay = day.toString().padStart(2, "0");
  const paddedMonth = month.toString().padStart(2, "0");

  return `${year}-${paddedMonth}-${paddedDay}`;
}

export const initializeBitrateCheckState = (): CheckboxState => {
  return Object.keys(AVAILABLE_BITRATES).reduce((acc, key) => {
    acc[key as keyof typeof AVAILABLE_BITRATES] = true;
    return acc;
  }, {} as CheckboxState);
};

export const validateUrl = (url: any) => {
  return url.includes("www.") && url.includes(".com");
};
export const presetJobHeader: PresetJobHeader[] = [
  {
    displayName: "#",
    columnSpacing: 1,
  },
  {
    displayName: "Preset Name",
    columnSpacing: 2,
  },
  {
    displayName: "Format",
    columnSpacing: 2,
  },
  {
    displayName: "Manifest File",
    columnSpacing: 4,
  },
  {
    displayName: "Copy",
    columnSpacing: 1,
  },
];

export const deepCompare = (
  initialContent: any,
  currentContent: any
): boolean => {
  if (initialContent === currentContent) return true;

  if (!initialContent || !currentContent) return false;

  if (Array.isArray(initialContent) && Array.isArray(currentContent)) {
    return compareArrays(initialContent, currentContent);
  }

  if (isObject(initialContent) && isObject(currentContent)) {
    return compareObjects(initialContent, currentContent);
  }

  return false;
};

const compareArrays = (initialArray: any[], currentArray: any[]): boolean => {
  if (initialArray.length !== currentArray.length) return false;

  for (let i = 0; i < initialArray.length; i++) {
    if (!deepCompare(initialArray[i], currentArray[i])) return false;
  }

  return true;
};

const compareObjects = (initialObj: any, currentObj: any): boolean => {
  const initialKeys = Object.keys(initialObj);
  const currentKeys = Object.keys(currentObj);

  if (initialKeys.length !== currentKeys.length) return false;

  for (const key of initialKeys) {
    if (!deepCompare(initialObj[key], currentObj[key])) return false;
  }

  return true;
};

const isObject = (value: any): value is object => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

export const mapResponseToGridData = (data: any) => {
  return (
    data?.map((item: any, index: number) => ({
      contentId: item?.job?.contentId,
      preset: item?.job?.preset?.name,
      inputFile: item?.job?.inputLink,
      outputFile: item?.job?.outputLink,
      status: item?.job?.state,
      progressPercent: item?.job?.progressPercentage,
      createdAt: item?.job?.createdAt,
      modifiedOn: item?.job?.modifiedOn,
      jobId: item?.job?.id,
      priority: item?.priority,
    })) || []
  );
};

export const commonStatusColumn = [
  { key: "id", displayName: "#" },
  { key: "contentId", displayName: "Content ID" },
  { key: "title", displayName: "Title" },
  { key: "preset", displayName: "Preset" },
  {
    key: "inputFile",
    displayName: "Input File",
    renderCell: (params: any) => {
      const formattedUrl = `${IMAGE_BASE_URL}${params?.row?.inputFile}`;
      const displayText =
        formattedUrl.length > 30
          ? `${formattedUrl.substring(0, 30)}...`
          : formattedUrl;
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            overflow: "hiddden",
            textOverflow: "ellipsis",
          }}
        >
          <Link
            sx={{ textDecoration: "none", color: "black", paddingRight: "5px" }}
            href={formattedUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {displayText}
          </Link>
          <button
            onClick={() => {
              handleCopyClick(params?.row?.inputFile);
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
            aria-label="copy-icons"
          >
            <img height={theme.spacing(4.5)} alt="copy" src={copyIcon} />
          </button>
        </Box>
      );
    },
  },
];

export const commonSubtitleColumn = [
  { key: "id", displayName: "#" },
  { key: "contentId", displayName: "Content ID" },
  { key: "title", displayName: "Title" },
  { key: "language", displayName: "Language" },
  {
    key: "inputFile",
    displayName: "Input File",
    renderCell: (params: any) => {
      const formattedUrl = `${IMAGE_BASE_URL}${params?.row?.inputFile}`;
      const displayText =
        formattedUrl.length > 30
          ? `${formattedUrl.substring(0, 30)}...`
          : formattedUrl;
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            overflow: "hiddden",
            textOverflow: "ellipsis",
          }}
        >
          <Link
            sx={{ textDecoration: "none", color: "black", paddingRight: "5px" }}
            href={formattedUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {displayText}
          </Link>
          <button
            onClick={() => {
              handleCopyClick(params?.row?.inputFile);
            }}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
            aria-label="copy-icons"
          >
            <img height={theme.spacing(4.5)} alt="copy" src={copyIcon} />
          </button>
        </Box>
      );
    },
  },
];

export const copyText = (text: string) => {
  CopyToClipboard(text);
};
export const handleCopyClick = (value: any) => {
  if (value && typeof value === "string") {
    if (value.startsWith("http://") || value.startsWith("https://")) {
      copyText(value);
    } else {
      copyText(`${IMAGE_BASE_URL}${value}`);
    }
  } else {
    console.error("Value is undefined or not a string");
  }
};

export const getJobsWithSequentialId = (
  allJobsDetails: JobDetails[],
  jobStatus: string,
  currentActivePage?: number
) => {
  return allJobsDetails
    ?.filter((item: JobDetails) => item?.status === jobStatus)
    ?.map((job: JobDetails, index: number) => ({
      ...job,
      id: ((currentActivePage || 0) - 1) * CONSTANTS.itemsPerPage + index + 1,
    }));
};

export const getPaginatedItems = (
  currentPage: any,
  itemsPerPage: any,
  allItems: any
) => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return allItems?.slice(startIndex, endIndex);
};

let uniqueIdCounter = 0;

export const generateUniqueId = (prefix = "id") => {
  uniqueIdCounter += 1;
  return `${prefix}-${uniqueIdCounter}-${Date.now()}`;
};

export const generateRandomId = (length = 8) => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export const generateLabels = (filter: string) => {
  const today = new Date();
  switch (filter) {
    case "today":
      const currentHour = today.getHours();
      return HOURLY_SHORTHANDS.slice(0, currentHour + 1);
    case "yesterday":
      return HOURLY_SHORTHANDS;
    case "last_7_days":
      return Array.from({ length: 7 })
        .map((_, i) => {
          const day = new Date(today);
          day.setDate(today.getDate() - i);
          return `${DAYS_SHORTHANDS[day.getDay()]} ${String(day.getDate()).padStart(2, '0')}`;
        })
        .reverse();
    case "last_30_days":
      return Array.from({ length: 30 })
        .map((_, i) => {
          const day = new Date(today);
          day.setDate(today.getDate() - (i + 1)); // -1 to exclude today and start from yesterday
          return day.toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
          });
        })
        .reverse();

    case "last_month":
      const lastMonth = new Date(today);
      lastMonth.setDate(1); // First day of the current month
      lastMonth.setMonth(today.getMonth() - 1); // Move to last month

      const daysInLastMonth = new Date(
        lastMonth.getFullYear(),
        lastMonth.getMonth() + 1,
        0
      ).getDate();

      return Array.from({ length: daysInLastMonth }).map((_, i) => {
        const date = new Date(lastMonth);
        date.setDate(i + 1);
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
        });
      });

    case "last_year":
      const lastYear = today.getFullYear() - 1;
      return Array.from({ length: 12 }).map((_, i) => {
        const date = new Date(lastYear, i, 1);
        return date.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });
      });

    default:
      return [];
  }
};

export const generateChartData = ({
  data,
  filter,
}: {
  data: GraphDataApiResponse[];
  filter: string;
}): number[] => {
  const today = new Date();

  switch (filter) {
    case "today":
      const currentHour = today.getHours();
      return HOURLY_SHORTHANDS.slice(0, currentHour + 1).map((hour) => {
        const entry = data.find((item) => item.time === hour);
        return entry ? entry.metric : 0;
      });

    case "yesterday":
      return HOURLY_SHORTHANDS.map((hour) => {
        const entry = data.find((item) => item.time === hour);
        return entry ? entry.metric : 0;
      });

    case "last_7_days":
      const last7Days = Array.from({ length: 7 })
        .map((_, i) => {
          const day = new Date(today);
          day.setDate(today.getDate() - i);
          return `${DAYS_SHORTHANDS[day.getDay()]} ${String(day.getDate()).padStart(2, '0')}`;
        })
        .reverse();

      return last7Days.map((day) => {
        const entry = data.find((item) => item.time.includes(day));
        return entry ? entry.metric : 0;
      });

    case "last_30_days":
      const last30Days = Array.from({ length: 30 })
        .map((_, i) => {
          const date = new Date(today);
          date.setDate(today.getDate() - (i + 1)); // From yesterday
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
          });
        })
        .reverse();

      return last30Days.map((date) => {
        const entry = data.find((item) => {
          return item.time.includes(date);
        });

        return entry ? entry.metric : 0;
      });

    case "last_month":
      const lastMonth = new Date(today);
      lastMonth.setMonth(today.getMonth() - 1);
      lastMonth.setDate(1);

      const daysInLastMonth = new Date(
        lastMonth.getFullYear(),
        lastMonth.getMonth() + 1,
        0
      ).getDate();

      const lastMonthDays = Array.from({ length: daysInLastMonth }).map(
        (_, i) => {
          const date = new Date(lastMonth);
          date.setDate(i + 1);
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
          });
        }
      );

      return lastMonthDays.map((date) => {
        const entry = data.find((item) => item.time.includes(date));
        return entry ? entry.metric : 0;
      });

    case "last_year":
      const lastYear = today.getFullYear() - 1;

      const lastYearMonths = Array.from({ length: 12 }).map((_, i) => {
        const date = new Date(lastYear, i, 1);
        return date.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        });
      });

      return lastYearMonths.map((month) => {
        const entry = data.find((item) => item.time.includes(month));
        return entry ? entry.metric : 0;
      });

    default:
      return [];
  }
};


export const isValidSemVer = (version: string) => {
  // Valid semver basic pattern: major.minor.patch optional extra, e.g. 1.2.3 or 1.2
  return /^(\d+)\.(\d+)(\.(\d+))?$/.test(version);
};

export const normalizeVersion = (version: string) => {
  const parts = version.split(".");
  if (parts.length === 2) {
    return version + ".0";
  }
  return version;
};

export const compareVersions = (v1: string, v2: string): number => {
  const nV1 = normalizeVersion(v1).split(".").map(Number);
  const nV2 = normalizeVersion(v2).split(".").map(Number);

  for (let i = 0; i < 3; i++) {
    if (nV1[i] > nV2[i]) return 1;
    if (nV1[i] < nV2[i]) return -1;
  }
  return 0;
};


